import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Read-only label/value pair for admin detail panels (e.g. the expandable
 * body-measurements row). Renders a monospace uppercase label above a
 * token-coloured value, with an em-dash placeholder for empty values and an
 * optional amber `accent` treatment for key metrics (fatPercent, leanLbs, …).
 *
 * Replaces the ad-hoc `<span class="fw-bold">Label:</span> {{ value }}`
 * Bootstrap pattern so admin detail rows stay on the design system. All visual
 * styling lives in this component's SCSS using design tokens.
 *
 * Attribute selector on `div` (not an element selector) so every caller renders
 * `<dl class="adm-detail-grid"><div bodyanalytics-adm-data-point>` — a real `<div>`
 * wrapping each `<dt>`/`<dd>` pair. axe-core's `definition-list` rule only treats
 * plain `<div>` children of `<dl>` as transparent wrappers around dt/dd; a custom
 * element tag (`<bodyanalytics-adm-data-point>`) does not qualify and fails the
 * "Ensure <dl> elements are structured correctly" check (#1788). Same pattern as
 * Angular Material's `tr[mat-row]`/`tr[mat-header-row]` used elsewhere in these
 * same admin tables.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'div[bodyanalytics-adm-data-point]',
  standalone: true,
  templateUrl: './adm-data-point.component.html',
  styleUrls: ['./adm-data-point.component.scss'],
})
export class AdmDataPointComponent {
  readonly label = input.required<string>();
  /** Pre-formatted display value. `null`/`undefined`/`''` render as `--`. */
  readonly value = input<string | number | null | undefined>(undefined);
  /** Highlight key metrics in the brand accent (amber). */
  readonly accent = input<boolean>(false);
  /**
   * Render the value in the data font (Geist Mono, tabular-nums) in the primary
   * ink — DESIGN.md requires it for numeric values, timestamps, and IDs that are
   * not the featured (accent) metric. Ignored visually when `accent` is set:
   * accent already implies the mono treatment plus the amber colour.
   */
  readonly mono = input<boolean>(false);

  protected readonly hasValue = computed(() => {
    const v = this.value();
    return v !== null && v !== undefined && `${v}`.trim() !== '';
  });
}
